// API Service för GraphQL-anrop
class ApiService {
  constructor() {
    this.baseURL = this.resolveBaseURL();
    // REST-bas (utan /query) för /api/*-endpoints (captcha, register, verify, admin).
    this.restBase = this.baseURL.replace(/\/query$/, '');
  }

  resolveBaseURL() {
    // I utveckling går anropen via dev-serverns proxy (undviker CORS).
    if (process.env.NODE_ENV === 'development') {
      return '/query';
    }
    // I produktion/test kan backend-URL:en sättas per miljö vid containerstart
    // (docker-entrypoint.sh → window.__APP_CONFIG__.wishlistApiUrl). Så kan
    // test-frontenden peka på api-test medan prod pekar på api, från samma image.
    const runtime = window.__APP_CONFIG__ && window.__APP_CONFIG__.wishlistApiUrl;
    if (runtime) {
      return runtime;
    }
    return 'https://api.brasse-pc.eu/query';
  }

  // Generiskt REST-anrop mot backendens /api/*-endpoints.
  async rest(path, { method = 'GET', body = null, token = null } = {}) {
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(this.restBase + path, {
      method,
      mode: 'cors',
      credentials: 'include',
      headers,
      body: body ? JSON.stringify(body) : null,
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.error || `HTTP ${response.status}`);
    }
    return data;
  }

  // --- Registrering (captcha + e-postverifiering) ---
  getPublicConfig() { return this.rest('/api/config'); }
  getCaptcha() { return this.rest('/api/captcha'); }
  registerUser(email, password, name, captchaId, captchaAnswer) {
    return this.rest('/api/register', { method: 'POST', body: { email, password, name, captchaId, captchaAnswer } });
  }
  verifyEmail(token) { return this.rest('/api/verify', { method: 'POST', body: { token } }); }
  resendVerification(email) { return this.rest('/api/resend', { method: 'POST', body: { email } }); }

  // --- Admin ---
  getAdminSettings(token) { return this.rest('/api/admin/settings', { token }); }
  updateAdminSettings(settings, token) { return this.rest('/api/admin/settings', { method: 'POST', body: settings, token }); }
  getPendingUsers(token) { return this.rest('/api/admin/pending', { token }); }
  approvePendingUser(id, token) { return this.rest('/api/admin/approve', { method: 'POST', body: { id }, token }); }
  rejectPendingUser(id, token) { return this.rest('/api/admin/reject', { method: 'POST', body: { id }, token }); }

  async request(query, variables = {}, token = null) {
    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      const response = await fetch(this.baseURL, {
        method: 'POST',
        mode: 'cors', // Explicit CORS-mode
        credentials: 'include', // Inkludera cookies för autentisering
        headers,
        body: JSON.stringify({
          query,
          variables
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      return result.data;
    } catch (error) {
      console.error('API Request failed:', error);
      throw error;
    }
  }

  // Registrera ny användare
  async register(email, password, name) {
    const query = `
      mutation RegisterUser($email: String!, $password: String!, $name: String!) {
        register(email: $email, password: $password, name: $name)
      }
    `;
    
    return this.request(query, { email, password, name });
  }

  // Logga in användare
  async login(email, password) {
    const query = `
      mutation LoginUser($email: String!, $password: String!) {
        login(email: $email, password: $password)
      }
    `;
    
    return this.request(query, { email, password });
  }

  // Hämta användarens önskelista
  async getWishlist(token) {
    const query = `
      query GetWishlist {
        wishlist {
          id
          name
          description
          url
          priority
          price
        }
      }
    `;
    
    return this.request(query, {}, token);
  }

  // Hämta alla användare och deras önskelistor (publikt)
  async getAllUsers(token = null) {
    const query = `
      query GetAllUsers {
        users {
          id
          email
          name
          wishlist {
            id
            name
            description
            url
            priority
            price
            assignments {
              id
              assignee {
                id
                name
                email
              }
              comment
            }
          }
        }
      }
    `;
    
    return this.request(query, {}, token);
  }

  // Hämta en specifik användare (publikt)
  async getUser(userId, token = null) {
    const query = `
      query GetUser($id: ID!) {
        user(id: $id) {
          id
          email
          name
          wishlist {
            id
            name
            description
            url
            priority
            price
            assignments {
              id
              assignee {
                id
                name
                email
              }
              comment
            }
          }
        }
      }
    `;
    
    return this.request(query, { id: userId }, token);
  }

  // Hämta användarprofil
  async getProfile(token) {
    const query = `
      query GetProfile {
        me {
          id
          email
          name
          wishlist {
            id
            name
            description
            url
            priority
            price
          }
        }
      }
    `;
    
    return this.request(query, {}, token);
  }

  // Hämta mina assignments
  async getMyAssignments(token) {
    const query = `
      query GetMyAssignments {
        myAssignments {
          id
          assignee {
            id
            name
            email
          }
          comment
        }
      }
    `;
    
    return this.request(query, {}, token);
  }

  // Lägg till item i önskelista
  async addItem(itemData, token) {
    const query = `
      mutation AddItem($name: String!, $description: String!, $url: String!, $priority: Int!, $price: Float) {
        addItem(name: $name, description: $description, url: $url, priority: $priority, price: $price) {
          id
          name
          description
          url
          priority
          price
        }
      }
    `;
    
    return this.request(query, itemData, token);
  }

  // Uppdatera item
  async updateItem(id, itemData, token) {
    const query = `
      mutation UpdateItem($id: ID!, $name: String, $description: String, $url: String, $priority: Int, $price: Float) {
        updateItem(id: $id, name: $name, description: $description, url: $url, priority: $priority, price: $price) {
          id
          name
          description
          url
          priority
          price
        }
      }
    `;
    
    return this.request(query, { id, ...itemData }, token);
  }

  // Ta bort item
  async deleteItem(id, token) {
    const query = `
      mutation DeleteItem($id: ID!) {
        deleteItem(id: $id)
      }
    `;
    
    return this.request(query, { id }, token);
  }

  // Assigna sig till ett item
  async assignToItem(itemId, comment, token) {
    const query = `
      mutation AssignToItem($itemId: ID!, $comment: String!) {
        assignToItem(itemId: $itemId, comment: $comment) {
          id
          assignee {
            id
            name
            email
          }
          comment
        }
      }
    `;
    
    return this.request(query, { itemId, comment }, token);
  }

  // Uppdatera assignment kommentar
  async updateAssignment(assignmentId, comment, token) {
    const query = `
      mutation UpdateAssignment($assignmentId: ID!, $comment: String!) {
        updateAssignment(assignmentId: $assignmentId, comment: $comment) {
          id
          assignee {
            id
            name
            email
          }
          comment
        }
      }
    `;
    
    return this.request(query, { assignmentId, comment }, token);
  }

  // Ta bort assignment
  async removeAssignment(assignmentId, token) {
    const query = `
      mutation RemoveAssignment($assignmentId: ID!) {
        removeAssignment(assignmentId: $assignmentId)
      }
    `;
    
    return this.request(query, { assignmentId }, token);
  }
}

export default new ApiService();
